import { useEffect } from 'react';

import { Container, Flex } from '@grupoboticario/flora-react';
import { MagazineIcon } from '@grupoboticario/flora-react-icons';
import { useParams } from 'react-router-dom';

import {
  CardContent,
  ErrorBoundaries,
  EmptyState,
  PageInfo,
  CardContentSkeleton,
  Skeleton,
  ResponsiveFloraTypography,
} from 'components';
import { useStarlinkContent } from 'hooks';
import { newRelicMonitoringService } from 'services';

import Styles from './ContentBuiltIn.styles';

import type { GenericContentProps, GenericContentStarlinkType } from 'domains/content';

type ContentBuiltInType = {
  document: string;
};

export const ContentBuiltIn = ({ document }: ContentBuiltInType) => {
  const { content: paramContent = '' } = useParams();
  const content = decodeURIComponent(paramContent);

  const {
    data: starlinkData,
    isLoading,
    isSuccess,
    isError,
  } = useStarlinkContent<GenericContentStarlinkType>({
    document,
  });

  const starlinkContent = starlinkData?.items as GenericContentStarlinkType[];
  const pageContent =
    starlinkContent &&
    (starlinkContent.find((item) => item.data.title === content)?.data as GenericContentProps);

  useEffect(() => {
    if (!isLoading) {
      newRelicMonitoringService.addPageAction(`pageview_${document}`, {
        cd_detail: `${isSuccess ? 'sucesso' : 'erro'}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess]);

  return (
    <Styles.Wrapper direction='column'>
      <Container size='large'>
        <PageInfo
          breadcrumbs={[
            {
              text: pageContent?.title || '',

              url: '',
            },
          ]}
        >
          {isLoading ? (
            <>
              <Skeleton height='$12' width='25%' />
              <Skeleton height='$7' width='50%' />
            </>
          ) : (
            <>
              <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold' as='h1'>
                {pageContent?.title}
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography>{pageContent?.description}</ResponsiveFloraTypography>
            </>
          )}
        </PageInfo>
      </Container>

      <Styles.SectionWrapper>
        <Container size='large'>
          <Flex direction='column'>
            {isLoading && (
              <Styles.SkeletonWrapper>
                {Array.from({ length: 3 }).map((_, index) => (
                  <CardContentSkeleton key={`skeleton-card-${index}-${pageContent?.title}`} />
                ))}
              </Styles.SkeletonWrapper>
            )}

            {isError && (
              <ErrorBoundaries.ReloadPage
                title='Tivemos um problema para exibir os conteúdos.'
                data-autotagging-flora-section={document}
              />
            )}

            {Boolean(!pageContent?.listToShare?.length) && isSuccess && (
              <Styles.EmptyStateWrapper justify='center'>
                <Styles.EmptyStateCard>
                  <EmptyState
                    title='Materiais em preparação.'
                    subtitle='Em breve, os conteúdos estarão disponíveis aqui.'
                  >
                    <MagazineIcon color='$backgroundAltPrimary' size={80} />
                  </EmptyState>
                </Styles.EmptyStateCard>
              </Styles.EmptyStateWrapper>
            )}

            {Boolean(pageContent?.listToShare?.length) && isSuccess && (
              <Styles.ListImagesWrapper>
                {pageContent?.listToShare?.map((item, index) => (
                  <CardContent
                    key={index}
                    {...item}
                    analyticsData={{
                      index: index + 1,
                      section: pageContent.title || pageContent.description || document || '',
                    }}
                  />
                ))}
              </Styles.ListImagesWrapper>
            )}
          </Flex>
        </Container>
      </Styles.SectionWrapper>
    </Styles.Wrapper>
  );
};
